import React, { useState, useRef } from "react"
import { KatexRenderer } from "@/components/shared/KatexRenderer"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Crosshair,
  Zap,
  Search,
  ImageIcon,
  XCircle,
  FileEdit,
  Clock,
  BookOpen,
  Layers,
  Maximize2,
  Download,
  Loader2,
  FileCode2,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { FullscreenMediaModal, ZoomableImage, downloadMedia } from "@/components/shared/FullscreenMediaModal"
import { SvgDiagramRenderer } from "@/components/shared/SvgDiagramRenderer"

export interface StructuredSolutionStep {
  step: number | string
  title?: string
  content: string
}

export interface DistractorItem {
  key: string
  text?: string
  status?: "correct" | "rejected" | string
  badge?: string
  flaw_type?: string
  explanation?: string
}

export interface SolutionBlock {
  id?: string
  type:
    | "callout"
    | "concept"
    | "method"
    | "derivation"
    | "shortcut"
    | "boundary_check"
    | "verification"
    | "visual_diagram"
    | "distractor_audit"
    | "audit_grid"
    | "scratchpad"
    | "toppers_notes"
    | string
  method_kind?: "derivation" | "shortcut" | "verification" | "visual_diagram" | "scratchpad" | string
  title?: string
  label?: string
  tab_label?: string
  icon?: string
  badge?: string
  method_number?: number
  estimated_time_seconds?: number
  diagram_type?: string
  render_format?: string
  content?: string
  svg_content?: string
  steps?: StructuredSolutionStep[]
  items?: DistractorItem[]
  options?: DistractorItem[]
  media_url?: string | null
  image_url?: string | null
}

export interface StructuredSolutionData {
  version?: string
  blocks?: SolutionBlock[]
  socratic_intuition?: string
  method_1_derivation?: string
  method_2_speed_shortcut?: string
  method_3_dimensional_check?: string
  method_visual_diagram?: string
  distractor_audit?: Record<string, string>
  toppers_notes?: string
}

interface Props {
  solution?: StructuredSolutionData | null
  fallbackExplanation?: string | null
  format?: string
  className?: string
  allowDownload?: boolean
}

type LucideIconComponent = React.ComponentType<{ className?: string }>

/**
 * Semantic type to icon mapping (dynamic, relies on schema enum rather than brittle string regex)
 */
const TYPE_ICON_MAP: Record<string, LucideIconComponent> = {
  shortcut: Zap,
  speed_shortcut: Zap,
  derivation: Crosshair,
  callout: Lightbulb,
  concept: Lightbulb,
  boundary_check: Search,
  verification: Search,
  visual_diagram: ImageIcon,
  audit_grid: XCircle,
  distractor_audit: XCircle,
  scratchpad: FileEdit,
  toppers_notes: FileEdit,
  method: BookOpen,
}

/**
 * Fallback display titles when dynamic solutions don't supply a custom title
 */
const TYPE_DEFAULT_LABELS: Record<string, string> = {
  shortcut: "Speed Shortcut",
  speed_shortcut: "Speed Shortcut",
  derivation: "Step-by-Step Solution",
  callout: "Key Concept",
  concept: "Key Concept",
  boundary_check: "Sanity & Verification",
  verification: "Sanity & Verification",
  visual_diagram: "Visual Solution",
  audit_grid: "Option Elimination",
  distractor_audit: "Option Elimination",
  scratchpad: "Topper's Takeaway",
  toppers_notes: "Topper's Takeaway",
  method: "Detailed Method",
}

/**
 * Checks if a block is an interactive SVG diagram or visual markup based on schema contract or content
 */
export function isSvgDiagramBlock(block: SolutionBlock): boolean {
  if (block.svg_content?.trim()) return true
  const fmt = block.render_format?.toLowerCase()
  if (fmt === "svg") return true
  const content = (block.content || "").trim()
  if (/<svg\b/i.test(content)) return true
  if (fmt === "html" && /<(?:div|span|table|section|article|svg)\b/i.test(content)) return true
  return false
}

/**
 * Short labels for navigation tabs
 */
const TYPE_SHORT_TAB_LABELS: Record<string, string> = {
  shortcut: "Shortcut",
  speed_shortcut: "Shortcut",
  derivation: "Step-by-Step",
  callout: "Concept",
  concept: "Concept",
  boundary_check: "Sanity Check",
  verification: "Sanity Check",
  visual_diagram: "Diagram",
  audit_grid: "Options",
  distractor_audit: "Options",
  scratchpad: "Topper's Note",
  toppers_notes: "Topper's Note",
  method: "Method",
}

/**
 * Resolves full title for a block:
 * 1. Preserves the generator's or author's custom title (never overwrites dynamic titles)
 * 2. If missing, falls back to semantic type default
 */
export function resolveBlockTitle(block: SolutionBlock, idx: number): string {
  const customTitle = (block.title || block.label || "").trim()
  if (customTitle) {
    return customTitle
  }
  const kind = (block.method_kind || block.type || "").toLowerCase()
  if (TYPE_DEFAULT_LABELS[kind]) {
    return TYPE_DEFAULT_LABELS[kind]
  }
  if (block.method_number) {
    return `Method ${block.method_number}`
  }
  return `Method ${idx + 1}`
}

/**
 * Resolves short label for tab pills:
 * 1. Uses explicit `tab_label` if provided by generator
 * 2. If `customTitle` is short (<= 16 chars), uses it
 * 3. Uses standard short label for known semantic types
 * 4. Takes first 2 words of long title, or falls back to Method N
 */
export function resolveTabLabel(block: SolutionBlock, idx: number): string {
  if (block.tab_label && block.tab_label.trim()) {
    return block.tab_label.trim()
  }

  // 1. High-priority semantic identity checks by block ID or specific badge
  const blockId = (block.id || "").toLowerCase()
  if (blockId === "toppers_notes" || blockId === "scratchpad") {
    return "Topper's Note"
  }
  if (blockId === "socratic_intuition" || blockId === "concept_cue") {
    return "Concept"
  }
  if (blockId === "solution_diagram" || blockId === "visual_diagram") {
    return "Diagram"
  }

  const customTitle = (block.title || block.label || "").trim()
  const kind = (block.method_kind || block.type || "").toLowerCase()

  // 2. If author provided short title, use it directly
  if (customTitle && customTitle.length <= 16) {
    return customTitle
  }

  // 3. Use semantic type short label if known
  if (TYPE_SHORT_TAB_LABELS[kind]) {
    return TYPE_SHORT_TAB_LABELS[kind]
  }

  // 4. Extract first 2 words if title is reasonable, stripping dangling slashes and punctuation
  if (customTitle) {
    const cleanedTitle = customTitle.replace(/[\/\\:,\-–—]+$/, "").trim()
    const words = cleanedTitle.split(/\s+/)
    if (words.length <= 2 && cleanedTitle.length <= 20) {
      return cleanedTitle
    }
    const shortPhrase = words.slice(0, 2).join(" ").replace(/[\/\\:,\-–—]+$/, "").trim()
    if (shortPhrase.length <= 16) {
      return shortPhrase
    }
  }

  if (block.method_number) {
    return `Method ${block.method_number}`
  }
  return `Method ${idx + 1}`
}

/**
 * Resolves icon based on semantic type
 */
function resolveBlockIcon(block: SolutionBlock): LucideIconComponent {
  const kind = (block.method_kind || block.type || "").toLowerCase()
  return TYPE_ICON_MAP[kind] || BookOpen
}

export function StructuredSolutionRenderer({
  solution,
  fallbackExplanation,
  format = "markdown",
  className = "",
  allowDownload = false,
}: Props) {
  const blocks: SolutionBlock[] = React.useMemo(() => {
    if (solution?.blocks && Array.isArray(solution.blocks) && solution.blocks.length > 0) {
      return solution.blocks
    }

    if (solution) {
      const generated: SolutionBlock[] = []
      if (solution.socratic_intuition) {
        generated.push({
          id: "socratic_intuition",
          type: "callout",
          badge: "CONCEPT CUE",
          title: "Concept & Intuition",
          content: solution.socratic_intuition,
        })
      }
      if (solution.method_1_derivation) {
        generated.push({
          id: "method_1_derivation",
          type: "derivation",
          method_number: 1,
          title: "Step-by-Step Derivation",
          content: solution.method_1_derivation,
        })
      }
      if (solution.method_2_speed_shortcut) {
        generated.push({
          id: "method_2_speed_shortcut",
          type: "shortcut",
          method_number: 2,
          estimated_time_seconds: 10,
          title: "Exam Hall Speed Shortcut",
          badge: "Speed Technique",
          content: solution.method_2_speed_shortcut,
        })
      }
      const method3 = solution.method_3_dimensional_check || (solution as any).method_3_verification
      if (method3) {
        generated.push({
          id: "method_3_verification",
          type: "verification",
          method_number: 3,
          title: "Sanity & Boundary Check",
          content: method3,
        })
      }
      if (solution.method_visual_diagram) {
        generated.push({
          id: "method_visual_diagram",
          type: "visual_diagram",
          method_number: 4,
          title: "Visual Diagram",
          content: solution.method_visual_diagram,
        })
      }
      const distractorSource = solution.distractor_audit || (solution as any).option_elimination
      if (distractorSource) {
        let items: DistractorItem[] = []
        if (Array.isArray(distractorSource)) {
          items = distractorSource.map((opt: any, idx: number) => {
            const expl = typeof opt.explanation === "string" ? opt.explanation : (typeof opt.text === "string" ? opt.text : "")
            const isCorrect = opt.status === "correct" || String(opt.badge || "").toUpperCase() === "CORRECT" || expl.toLowerCase().includes("correct")
            return {
              key: String(opt.key || String.fromCharCode(65 + idx)),
              text: expl,
              explanation: expl,
              status: isCorrect ? "correct" : "rejected",
              badge: opt.badge,
              flaw_type: opt.flaw_type,
            }
          })
        } else if (distractorSource.options && Array.isArray(distractorSource.options)) {
          items = distractorSource.options.map((opt: any, idx: number) => {
            const expl = typeof opt.explanation === "string" ? opt.explanation : (typeof opt.text === "string" ? opt.text : "")
            const isCorrect = opt.status === "correct" || String(opt.badge || "").toUpperCase() === "CORRECT" || expl.toLowerCase().includes("correct")
            return {
              key: String(opt.key || String.fromCharCode(65 + idx)),
              text: expl,
              explanation: expl,
              status: isCorrect ? "correct" : "rejected",
              badge: opt.badge,
              flaw_type: opt.flaw_type,
            }
          })
        } else if (typeof distractorSource === "object") {
          items = Object.entries(distractorSource).map(([key, val]: [string, any]) => {
            if (val && typeof val === "object" && !Array.isArray(val)) {
              const expl = typeof val.explanation === "string" ? val.explanation : (typeof val.text === "string" ? val.text : (typeof val.reason === "string" ? val.reason : ""))
              const isCorrect = val.status === "correct" || String(val.badge || "").toUpperCase() === "CORRECT" || expl.toLowerCase().includes("correct")
              return {
                key: String(val.key || key),
                text: expl,
                explanation: expl,
                status: isCorrect ? "correct" : "rejected",
                badge: val.badge,
                flaw_type: val.flaw_type,
              }
            }
            const textStr = typeof val === "string" ? val : (Array.isArray(val) ? val.join(" ") : String(val ?? ""))
            const isCorrect = textStr.toLowerCase().includes("correct")
            return {
              key,
              text: textStr,
              explanation: textStr,
              status: isCorrect ? "correct" : "rejected",
            }
          })
        }

        if (items.length > 0) {
          generated.push({
            id: "distractor_audit",
            type: "distractor_audit",
            title: "Option Elimination & Analysis",
            items,
          })
        }
      }
      if (solution.toppers_notes) {
        generated.push({
          id: "toppers_notes",
          type: "scratchpad",
          title: "Topper's Key Takeaway",
          content: solution.toppers_notes,
        })
      }
      if (generated.length > 0) return generated
    }

    return []
  }, [solution])

  // Active tab defaults to first block, or 'all' if user chooses
  const defaultTab = blocks.length > 0 ? (blocks[0].id || "block_0") : "all"
  const [activeTab, setActiveTab] = useState<string>(defaultTab)
  const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({})

  const toggleBlock = (id: string) => {
    setCollapsedMap((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const expandAll = () => {
    const updated: Record<string, boolean> = {}
    blocks.forEach((_, idx) => { updated[`block_${idx}`] = false })
    setCollapsedMap(updated)
  }

  const collapseAll = () => {
    const updated: Record<string, boolean> = {}
    blocks.forEach((_, idx) => { updated[`block_${idx}`] = true })
    setCollapsedMap(updated)
  }

  if (blocks.length === 0) {
    if (!fallbackExplanation) return null
    return (
      <div className={`rounded-2xl border border-border/80 bg-card shadow-xs overflow-hidden ${className}`}>
        <div className="flex items-center gap-2.5 px-3.5 sm:px-5 py-3 bg-muted/30 border-b border-border/50">
          <div className="w-6 h-6 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
            <Lightbulb className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-foreground">Solution Explanation</span>
        </div>
        <div className="p-3.5 sm:p-5 max-w-4xl">
          <KatexRenderer content={fallbackExplanation} format={format} className="text-xs sm:text-sm leading-relaxed text-foreground" />
        </div>
      </div>
    )
  }

  const selectedBlockIndex = blocks.findIndex((b, idx) => (b.id || `block_${idx}`) === activeTab)
  const activeBlock = selectedBlockIndex !== -1 ? blocks[selectedBlockIndex] : blocks[0]
  const activeBlockKey = activeBlock?.id || `block_${Math.max(0, selectedBlockIndex)}`

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
        <div className="flex items-center gap-2">
          <div className="w-1 h-3.5 rounded-full bg-primary" />
          <span className="text-xs font-bold tracking-wide text-foreground">
            Solution & Analysis
          </span>
          <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-semibold bg-primary/10 text-primary border-primary/25 gap-1">
            <CheckCircle2 className="h-3 w-3" />
            {blocks.length > 1 ? `${blocks.length} Approaches` : "Verified Solution"}
          </Badge>
        </div>

        {activeTab === "all" ? (
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="sm" onClick={expandAll}
              className="h-6 px-2 text-[10px] sm:text-[11px] font-medium text-muted-foreground hover:text-foreground">
              Expand All
            </Button>
            <span className="text-muted-foreground/50 text-xs">·</span>
            <Button type="button" variant="ghost" size="sm" onClick={collapseAll}
              className="h-6 px-2 text-[10px] sm:text-[11px] font-medium text-muted-foreground hover:text-foreground">
              Collapse All
            </Button>
          </div>
        ) : (
          <span className="text-[10px] sm:text-[11px] text-muted-foreground font-medium hidden sm:inline">
            Select method above to switch view
          </span>
        )}
      </div>

      {/* Method Switcher Navigation: Swipeable Pill Chips */}
      {blocks.length > 1 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 touch-pan-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {blocks.map((b, idx) => {
            const key = b.id || `block_${idx}`
            const tabLabel = resolveTabLabel(b, idx)
            const TabIcon = resolveBlockIcon(b)
            const isSelected = activeTab === key

            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition-all cursor-pointer ${
                  isSelected
                    ? "bg-primary text-primary-fg shadow-2xs border border-primary/40 font-extrabold"
                    : "bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 border border-border/60"
                }`}
              >
                <TabIcon className={`h-3.5 w-3.5 ${isSelected ? "text-primary-fg" : "text-muted-foreground"}`} />
                <span className="whitespace-nowrap">{tabLabel}</span>
                {b.estimated_time_seconds && (
                  <span className={`text-[9px] px-1 py-0.2 rounded font-mono ${
                    isSelected ? "bg-primary-fg/20 text-primary-fg" : "bg-muted text-muted-foreground"
                  }`}>
                    {b.estimated_time_seconds}s
                  </span>
                )}
              </button>
            )
          })}

          {/* All Methods Pill */}
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition-all cursor-pointer ${
              activeTab === "all"
                ? "bg-primary text-primary-fg shadow-2xs border border-primary/40 font-extrabold"
                : "bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 border border-border/60"
            }`}
          >
            <Layers className={`h-3.5 w-3.5 ${activeTab === "all" ? "text-primary-fg" : "text-muted-foreground"}`} />
            <span className="whitespace-nowrap">All Methods</span>
            <span className={`text-[9px] px-1 rounded ${activeTab === "all" ? "bg-primary-fg/20" : "bg-muted"}`}>
              {blocks.length}
            </span>
          </button>
        </div>
      )}

      {/* Content Rendering: Focused View or Stacked Accordion */}
      {activeTab !== "all" ? (
        /* Focused Single Method View */
        <FocusedBlockView
          block={activeBlock}
          blockKey={activeBlockKey}
          blockIndex={selectedBlockIndex !== -1 ? selectedBlockIndex : 0}
          format={format}
          allowDownload={allowDownload}
        />
      ) : (
        /* All Methods Stacked View */
        <div className="space-y-2.5">
          {blocks.map((block, idx) => {
            const blockKey = block.id || `block_${idx}`
            const isCollapsed = collapsedMap[blockKey] ?? false
            return (
              <BlockCard
                key={blockKey}
                blockKey={blockKey}
                blockIndex={idx}
                block={block}
                isCollapsed={isCollapsed}
                onToggle={() => toggleBlock(blockKey)}
                format={format}
                allowDownload={allowDownload}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Focused Single Method View (Rendered when a specific tab is chosen)
 */
function FocusedBlockView({
  block,
  blockKey: _blockKey,
  blockIndex,
  format,
  allowDownload = false,
}: {
  block: SolutionBlock
  blockKey: string
  blockIndex: number
  format: string
  allowDownload?: boolean
}) {
  const { id, type, method_kind, badge, estimated_time_seconds, content, steps, items, options } = block

  const auditItems = (options && options.length > 0) ? options : items
  const kind = (method_kind || type || "").toLowerCase()
  const isScratchpad =
    id === "toppers_notes" ||
    kind === "scratchpad" ||
    kind === "toppers_notes"

  const isSpeedShortcut = kind === "shortcut" || kind === "speed_shortcut"
  const isVisualDiagram =
    kind === "visual_diagram" ||
    kind === "diagram" ||
    (typeof id === "string" && (id.includes("visual_diagram") || id.includes("diagram")))

  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const diagramRef = useRef<HTMLDivElement>(null)

  const handleDirectDownload = async (format: "png" | "svg" | "jpeg" = "png") => {
    if (downloading) return
    setDownloading(true)
    const baseName = `solution-diagram-${block.id || blockIndex + 1}`
    try {
      await downloadMedia({
        url: block.media_url || block.image_url,
        element: diagramRef.current,
        filename: baseName,
        format,
      })
    } finally {
      setDownloading(false)
    }
  }

  const getStyleTokens = () => {
    if (isScratchpad) {
      return {
        border: "border-tertiary/35",
        headerBg: "bg-tertiary/6",
        iconBg: "bg-tertiary/15",
        iconColor: "text-tertiary",
        badgeClass: "bg-tertiary/15 text-tertiary border-tertiary/30",
      }
    }
    if (isSpeedShortcut) {
      return {
        border: "border-tertiary/35",
        headerBg: "bg-tertiary/6",
        iconBg: "bg-tertiary/15",
        iconColor: "text-tertiary",
        badgeClass: "bg-tertiary/15 text-tertiary border-tertiary/30",
      }
    }
    if (isVisualDiagram) {
      return {
        border: "border-primary/30",
        headerBg: "bg-primary/5",
        iconBg: "bg-primary/15",
        iconColor: "text-primary",
        badgeClass: "bg-primary/12 text-primary border-primary/25",
      }
    }
    switch (kind) {
      case "derivation":
        return {
          border: "border-border/80",
          headerBg: "bg-muted/30",
          iconBg: "bg-primary/15",
          iconColor: "text-primary",
          badgeClass: "bg-primary/12 text-primary border-primary/25",
        }
      case "verification":
      case "boundary_check":
        return {
          border: "border-secondary/25",
          headerBg: "bg-secondary/5",
          iconBg: "bg-secondary/15",
          iconColor: "text-secondary",
          badgeClass: "bg-secondary/12 text-secondary border-secondary/25",
        }
      case "distractor_audit":
      case "audit_grid":
        return {
          border: "border-border/80",
          headerBg: "bg-muted/30",
          iconBg: "bg-muted/70",
          iconColor: "text-muted-foreground",
          badgeClass: "bg-muted/60 text-muted-foreground border-border/50",
        }
      case "callout":
      case "concept":
        return {
          border: "border-primary/25",
          headerBg: "bg-primary/5",
          iconBg: "bg-primary/15",
          iconColor: "text-primary",
          badgeClass: "bg-primary/12 text-primary border-primary/25",
        }
      default:
        return {
          border: "border-border/80",
          headerBg: "bg-muted/30",
          iconBg: "bg-muted/60",
          iconColor: "text-muted-foreground",
          badgeClass: "bg-muted/60 text-muted-foreground border-border/50",
        }
    }
  }

  const styles = getStyleTokens()
  const IconComponent = resolveBlockIcon(block)
  const displayLabel = resolveBlockTitle(block, blockIndex)

  return (
    <div className={`rounded-2xl border ${styles.border} bg-card shadow-xs overflow-hidden transition-all duration-200`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-3.5 sm:px-5 py-3 sm:py-3.5 ${styles.headerBg} border-b border-border/50`}>
        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
          <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg ${styles.iconBg} flex items-center justify-center shrink-0 shadow-2xs`}>
            <IconComponent className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${styles.iconColor}`} />
          </div>

          <span className="text-xs sm:text-sm font-bold text-foreground">
            {displayLabel}
          </span>

          {badge && (
            <Badge variant="outline" className={`text-[9px] sm:text-[10px] px-2 py-0.5 font-bold ${styles.badgeClass}`}>
              {badge}
            </Badge>
          )}

          {isSpeedShortcut && (
            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-2 py-0.5 font-extrabold bg-tertiary/15 text-tertiary border-tertiary/30">
              Speed Technique
            </Badge>
          )}

          {estimated_time_seconds && (
            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-2 py-0.5 font-mono flex items-center gap-1 bg-muted/60 text-muted-foreground border-border/50">
              <Clock className="w-2.5 h-2.5" />
              {estimated_time_seconds}s
            </Badge>
          )}
        </div>

        {/* Full Screen & Download Actions for Diagrams and Media */}
        <div className="flex items-center gap-1.5">
          {allowDownload && (isVisualDiagram || block.media_url || block.image_url) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={downloading}
                  className="h-7 px-2.5 gap-1.5 text-[11px] font-semibold text-secondary hover:text-secondary border-secondary/40 hover:bg-secondary/10 bg-background/80 shadow-2xs"
                  title="Download diagram in multiple formats (Admin only)"
                >
                  {downloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  <span>{downloading ? "Exporting…" : "Download"}</span>
                  <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 text-xs font-medium">
                <DropdownMenuItem
                  onClick={() => handleDirectDownload("svg")}
                  className="gap-2 cursor-pointer text-foreground hover:text-secondary font-medium"
                >
                  <FileCode2 className="h-3.5 w-3.5 text-secondary" />
                  <span>Vector SVG (.svg)</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDirectDownload("png")}
                  className="gap-2 cursor-pointer text-foreground hover:text-primary font-medium"
                >
                  <ImageIcon className="h-3.5 w-3.5 text-primary" />
                  <span>High-Res PNG (.png)</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDirectDownload("jpeg")}
                  className="gap-2 cursor-pointer text-foreground hover:text-foreground font-medium"
                >
                  <FileCode2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Compressed JPEG (.jpg)</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {(isVisualDiagram || block.media_url || block.image_url) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFullscreenOpen(true)}
              className="h-7 px-2.5 gap-1.5 text-[11px] font-semibold bg-background/80 hover:bg-muted text-foreground border-border/70 shadow-2xs"
              title="Open full screen"
            >
              <Maximize2 className="h-3.5 w-3.5 text-primary" />
              <span>Full Screen</span>
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div
        ref={isVisualDiagram ? diagramRef : undefined}
        className="p-3.5 sm:p-5 md:p-6 space-y-3.5 text-xs sm:text-sm leading-relaxed text-foreground max-w-4xl"
      >
        {/* Speed Shortcut Highlight Strip */}
        {isSpeedShortcut && (
          <div className="flex items-center gap-2 p-2.5 sm:p-3 rounded-xl bg-tertiary/10 border border-tertiary/25 text-xs text-tertiary font-medium">
            <Zap className="h-3.5 w-3.5 shrink-0 text-tertiary" />
            <span><strong>Exam Hall Shortcut:</strong> High-yield technique optimized to save critical seconds during actual exam conditions.</span>
          </div>
        )}

        {/* Visual Diagram Image if attached */}
        {(block.media_url || block.image_url) && (
          <ZoomableImage
            src={(block.media_url || block.image_url)!}
            alt={block.title || "Visual Diagram"}
            title={displayLabel || "Visual Diagram"}
            caption="Visual Diagram (Click to view full screen)"
            className="max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
            allowDownload={allowDownload}
            downloadFilename={`solution-diagram-${block.id || blockIndex + 1}`}
          />
        )}

        {/* Distractor Audit Grid */}
        {auditItems && auditItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
            {auditItems.map((it, itIdx) => {
              const isCorrect =
                it.status === "correct" ||
                String(it.badge || "").toUpperCase() === "CORRECT" ||
                (typeof it.explanation === "string" && it.explanation.toLowerCase().startsWith("correct")) ||
                (typeof it.text === "string" && it.text.toLowerCase().startsWith("correct"))
              return (
                <div
                  key={itIdx}
                  className={`p-3 sm:p-3.5 rounded-xl border flex flex-col justify-between gap-2.5 transition-all ${
                    isCorrect
                      ? "bg-secondary/8 border-secondary/25"
                      : "bg-muted/30 border-border/50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold shrink-0 ${
                          isCorrect
                            ? "bg-secondary text-secondary-fg shadow-2xs"
                            : "bg-muted text-muted-foreground border border-border/60"
                        }`}
                      >
                        {it.key}
                      </span>
                      <span className={`text-[11px] sm:text-xs font-semibold ${isCorrect ? "text-secondary font-bold" : "text-foreground"}`}>
                        ({it.key}) {it.flaw_type || (isCorrect ? "Correct Answer" : "Rejected")}
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[8px] sm:text-[9px] px-1.5 sm:px-2 py-0.5 font-bold uppercase shrink-0 ${
                        isCorrect
                          ? "bg-secondary/15 text-secondary border-secondary/30"
                          : "bg-muted/60 text-muted-foreground border-border/50"
                      }`}
                    >
                      {it.badge || (isCorrect ? "CORRECT" : "FLAW")}
                    </Badge>
                  </div>
                  <div className="text-[11px] sm:text-xs leading-relaxed text-foreground/90 pl-0.5">
                    <KatexRenderer content={it.explanation || it.text || ""} format={format} disableDirectionCallout={true} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : isSvgDiagramBlock(block) ? (
          <SvgDiagramRenderer
            content={block.svg_content || block.content || ""}
            title={displayLabel || "Visual Diagram"}
            allowDownload={false}
            allowFullscreen={false}
            downloadFilename={`solution-diagram-${block.id || blockIndex + 1}`}
          />
        ) : content ? (
          isScratchpad ? (
            /* Topper Scratchpad */
            <div className="rounded-xl border border-tertiary/30 bg-tertiary/5 p-3.5 sm:p-4 space-y-1.5">
              <div className="flex items-center gap-2 mb-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-tertiary">
                <FileEdit className="h-3.5 w-3.5" />
                <span>Topper's Key Takeaway</span>
              </div>
              <KatexRenderer
                content={content}
                format={format}
                disableDirectionCallout={true}
                className="text-xs sm:text-sm leading-relaxed text-foreground font-medium"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <KatexRenderer content={content} format={format} disableDirectionCallout={true} className="text-xs sm:text-sm leading-relaxed" />
            </div>
          )
        ) : steps && steps.length > 0 ? (
          <div className="space-y-2 sm:space-y-2.5">
            {steps.map((st, sIdx) => (
              <div key={sIdx} className="flex items-start gap-2.5 sm:gap-3 p-3 sm:p-3.5 rounded-xl bg-muted/30 border border-border/50">
                <Badge
                  variant="outline"
                  className="shrink-0 px-2 sm:px-2.5 py-0.5 text-[9px] sm:text-[10px] font-mono font-bold tracking-wider bg-primary/10 text-primary border-primary/25 rounded-lg"
                >
                  {st.step || sIdx + 1}
                </Badge>
                <div className="flex-1 min-w-0 space-y-1">
                  {st.title && (
                    <div className="font-semibold text-foreground text-xs sm:text-sm">{st.title}</div>
                  )}
                  <KatexRenderer content={st.content} format={format} disableDirectionCallout={true} className="text-xs sm:text-sm" />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <FullscreenMediaModal
        isOpen={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        title={displayLabel || "Visual Diagram"}
        src={block.media_url || block.image_url}
        allowDownload={allowDownload}
        downloadFilename={`solution-diagram-${block.id || blockIndex + 1}`}
      >
        {!(block.media_url || block.image_url) && isSvgDiagramBlock(block) && (
          <div className="w-full flex items-center justify-center [&>div]:!max-w-none [&>div]:!w-full [&>div>div]:!max-w-none [&>div>div]:!w-full [&_svg]:!w-full [&_svg]:!max-h-[75vh] [&_svg]:!h-auto">
            <SvgDiagramRenderer
              content={block.svg_content || block.content || ""}
              title={displayLabel || "Visual Diagram"}
              allowFullscreen={false}
              allowDownload={false}
              className="w-full flex items-center justify-center"
            />
          </div>
        )}
        {!(block.media_url || block.image_url) && !isSvgDiagramBlock(block) && content && (
          <div className="space-y-4 max-w-4xl mx-auto p-4 bg-card rounded-xl border border-border">
            <KatexRenderer content={content} format={format} disableDirectionCallout={true} className="text-sm leading-relaxed" />
          </div>
        )}
      </FullscreenMediaModal>
    </div>
  )
}

/**
 * Accordion Card View (Used in "All Methods" mode)
 */
function BlockCard({
  blockKey: _blockKey,
  blockIndex,
  block,
  isCollapsed,
  onToggle,
  format,
  allowDownload = false,
}: {
  blockKey: string
  blockIndex: number
  block: SolutionBlock
  isCollapsed: boolean
  onToggle: () => void
  format: string
  allowDownload?: boolean
}) {
  const { id, type, method_kind, badge, estimated_time_seconds, content, steps, items, options } = block

  const auditItems = (options && options.length > 0) ? options : items
  const kind = (method_kind || type || "").toLowerCase()
  const isScratchpad =
    id === "toppers_notes" ||
    kind === "scratchpad" ||
    kind === "toppers_notes"

  const isSpeedShortcut = kind === "shortcut" || kind === "speed_shortcut"
  const isVisualDiagram =
    kind === "visual_diagram" ||
    kind === "diagram" ||
    id === "visual_diagram" ||
    id === "method_visual_diagram" ||
    id === "solution_diagram" ||
    id === "method_4_visual_diagram"

  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const cardDiagramRef = useRef<HTMLDivElement>(null)

  const handleDirectDownload = async () => {
    if (downloading) return
    setDownloading(true)
    const baseName = `solution-diagram-${block.id || blockIndex + 1}`
    try {
      await downloadMedia({
        url: block.media_url || block.image_url,
        element: cardDiagramRef.current,
        filename: baseName,
      })
    } finally {
      setDownloading(false)
    }
  }

  const getStyleTokens = () => {
    if (isScratchpad) {
      return {
        border: "border-tertiary/30",
        headerBg: "bg-tertiary/6 hover:bg-tertiary/10",
        iconBg: "bg-tertiary/15",
        iconColor: "text-tertiary",
        badgeClass: "bg-tertiary/15 text-tertiary border-tertiary/30",
        timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
      }
    }
    if (isSpeedShortcut) {
      return {
        border: "border-tertiary/30",
        headerBg: "bg-tertiary/6 hover:bg-tertiary/10",
        iconBg: "bg-tertiary/15",
        iconColor: "text-tertiary",
        badgeClass: "bg-tertiary/15 text-tertiary border-tertiary/30",
        timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
      }
    }
    if (isVisualDiagram) {
      return {
        border: "border-primary/30",
        headerBg: "bg-primary/5 hover:bg-primary/8",
        iconBg: "bg-primary/15",
        iconColor: "text-primary",
        badgeClass: "bg-primary/12 text-primary border-primary/25",
        timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
      }
    }
    switch (kind) {
      case "derivation":
        return {
          border: "border-border/80",
          headerBg: "bg-muted/25 hover:bg-muted/40",
          iconBg: "bg-primary/15",
          iconColor: "text-primary",
          badgeClass: "bg-primary/12 text-primary border-primary/25",
          timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
        }
      case "verification":
      case "boundary_check":
        return {
          border: "border-secondary/20",
          headerBg: "bg-secondary/5 hover:bg-secondary/8",
          iconBg: "bg-secondary/15",
          iconColor: "text-secondary",
          badgeClass: "bg-secondary/12 text-secondary border-secondary/25",
          timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
        }
      case "distractor_audit":
      case "audit_grid":
        return {
          border: "border-border/70",
          headerBg: "bg-muted/25 hover:bg-muted/40",
          iconBg: "bg-muted/60",
          iconColor: "text-muted-foreground",
          badgeClass: "bg-muted/60 text-muted-foreground border-border/50",
          timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
        }
      case "callout":
      case "concept":
        return {
          border: "border-primary/20",
          headerBg: "bg-primary/5 hover:bg-primary/8",
          iconBg: "bg-primary/15",
          iconColor: "text-primary",
          badgeClass: "bg-primary/12 text-primary border-primary/25",
          timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
        }
      default:
        return {
          border: "border-border/70",
          headerBg: "bg-muted/25 hover:bg-muted/40",
          iconBg: "bg-muted/60",
          iconColor: "text-muted-foreground",
          badgeClass: "bg-muted/60 text-muted-foreground border-border/50",
          timeBadge: "bg-muted/60 text-muted-foreground border-border/50",
        }
    }
  }

  const styles = getStyleTokens()
  const IconComponent = resolveBlockIcon(block)
  const displayLabel = resolveBlockTitle(block, blockIndex)

  return (
    <div className={`rounded-2xl border ${styles.border} bg-card shadow-xs overflow-hidden transition-all duration-200`}>
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-3.5 sm:px-5 py-2.5 sm:py-3 text-left ${styles.headerBg} transition-colors cursor-pointer`}
      >
        <div className="flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0 pr-2">
          <div className={`w-6 h-6 rounded-md ${styles.iconBg} flex items-center justify-center shrink-0`}>
            <IconComponent className={`h-3.5 w-3.5 ${styles.iconColor}`} />
          </div>

          <span className="text-xs sm:text-sm font-semibold truncate text-foreground">
            {displayLabel}
          </span>

          {badge && (
            <Badge variant="outline" className={`text-[9px] sm:text-[10px] px-1.5 py-0 h-4 font-semibold ${styles.badgeClass}`}>
              {badge}
            </Badge>
          )}

          {isScratchpad && (
            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1.5 py-0 h-4 font-semibold bg-tertiary/15 text-tertiary border-tertiary/30">
              Key Insight
            </Badge>
          )}

          {isSpeedShortcut && (
            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1.5 py-0 h-4 font-semibold bg-tertiary/15 text-tertiary border-tertiary/30">
              Speed Technique
            </Badge>
          )}

          {estimated_time_seconds && (
            <Badge variant="outline" className={`text-[9px] sm:text-[10px] px-1.5 py-0 h-4 font-mono flex items-center gap-1 ${styles.timeBadge}`}>
              <Clock className="w-2.5 h-2.5" />
              {estimated_time_seconds}s
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {allowDownload && (isVisualDiagram || block.media_url || block.image_url) && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                handleDirectDownload()
              }}
              disabled={downloading}
              className="h-6 px-2 gap-1 text-[10px] font-semibold text-secondary hover:text-secondary border-secondary/40 hover:bg-secondary/10 bg-background/80 shadow-2xs"
              title="Download diagram image (Admin only)"
            >
              {downloading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Download className="h-3 w-3" />
              )}
              <span className="hidden sm:inline">{downloading ? "..." : "Download"}</span>
            </Button>
          )}

          {(isVisualDiagram || block.media_url || block.image_url) && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                setFullscreenOpen(true)
              }}
              className="h-6 px-2 gap-1 text-[10px] font-semibold bg-background/80 hover:bg-muted text-foreground border-border/70 shadow-2xs"
              title="Open full screen"
            >
              <Maximize2 className="h-3 w-3 text-primary" />
              <span className="hidden sm:inline">Full Screen</span>
            </Button>
          )}
          <div className="shrink-0 text-muted-foreground">
            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </div>
        </div>
      </button>

      {/* Body */}
      {!isCollapsed && (
        <div
          ref={isVisualDiagram ? cardDiagramRef : undefined}
          className="p-3.5 sm:p-5 space-y-3 text-xs sm:text-sm leading-relaxed text-foreground max-w-4xl"
        >
          {/* Visual Diagram Image if attached */}
          {(block.media_url || block.image_url) && (
            <ZoomableImage
              src={(block.media_url || block.image_url)!}
              alt={block.title || "Visual Diagram"}
              title={displayLabel || "Visual Diagram"}
              caption="Visual Diagram (Click to view full screen)"
              className="max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
              allowDownload={allowDownload}
              downloadFilename={`solution-diagram-${block.id || blockIndex + 1}`}
            />
          )}

          {/* Distractor Audit Grid */}
          {auditItems && auditItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {auditItems.map((it, itIdx) => {
                const isCorrect =
                  it.status === "correct" ||
                  String(it.badge || "").toUpperCase() === "CORRECT" ||
                  (typeof it.explanation === "string" && it.explanation.toLowerCase().startsWith("correct")) ||
                  (typeof it.text === "string" && it.text.toLowerCase().startsWith("correct"))
                return (
                  <div
                    key={itIdx}
                    className={`p-3 sm:p-3.5 rounded-xl border flex flex-col justify-between gap-2 transition-all ${
                      isCorrect
                        ? "bg-secondary/8 border-secondary/25"
                        : "bg-muted/30 border-border/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold shrink-0 ${
                            isCorrect
                              ? "bg-secondary text-secondary-fg"
                              : "bg-muted text-muted-foreground border border-border/60"
                          }`}
                        >
                          {it.key}
                        </span>
                        <span className={`text-[11px] sm:text-xs font-semibold ${isCorrect ? "text-secondary font-bold" : "text-foreground"}`}>
                          ({it.key}) {it.flaw_type || (isCorrect ? "Correct Answer" : "Rejected")}
                        </span>
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-[8px] sm:text-[9px] px-1.5 sm:px-2 py-0.5 font-bold uppercase shrink-0 ${
                          isCorrect
                            ? "bg-secondary/15 text-secondary border-secondary/30"
                            : "bg-muted/60 text-muted-foreground border-border/50"
                        }`}
                      >
                        {it.badge || (isCorrect ? "CORRECT" : "FLAW")}
                      </Badge>
                    </div>
                    <div className="text-[11px] sm:text-xs leading-relaxed text-foreground/90 pl-0.5">
                      <KatexRenderer content={it.explanation || it.text || ""} format={format} disableDirectionCallout={true} />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : isSvgDiagramBlock(block) ? (
            <SvgDiagramRenderer
              content={block.svg_content || block.content || ""}
              title={displayLabel || "Visual Diagram"}
              allowDownload={false}
              allowFullscreen={false}
              downloadFilename={`solution-diagram-${block.id || blockIndex + 1}`}
            />
          ) : content ? (
            isScratchpad ? (
              /* Topper Scratchpad */
              <div className="rounded-xl border border-tertiary/30 bg-tertiary/5 p-3.5 sm:p-4 space-y-1">
                <div className="flex items-center gap-2 mb-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-tertiary">
                  <FileEdit className="h-3.5 w-3.5" />
                  <span>Key Insight</span>
                </div>
                <KatexRenderer
                  content={content}
                  format={format}
                  disableDirectionCallout={true}
                  className="text-xs sm:text-sm leading-relaxed text-foreground font-medium"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <KatexRenderer content={content} format={format} disableDirectionCallout={true} className="text-xs sm:text-sm leading-relaxed" />
              </div>
            )
          ) : steps && steps.length > 0 ? (
            <div className="space-y-2">
              {steps.map((st, sIdx) => (
                <div key={sIdx} className="flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg bg-muted/30 border border-border/50">
                  <Badge
                    variant="outline"
                    className="shrink-0 px-2 py-0.5 text-[9px] sm:text-[10px] font-mono font-bold tracking-wider bg-primary/10 text-primary border-primary/25 rounded-md"
                  >
                    {st.step || sIdx + 1}
                  </Badge>
                  <div className="flex-1 min-w-0 space-y-1">
                    {st.title && (
                      <div className="font-semibold text-foreground text-xs sm:text-sm">{st.title}</div>
                    )}
                    <KatexRenderer content={st.content} format={format} disableDirectionCallout={true} className="text-xs sm:text-sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      <FullscreenMediaModal
        isOpen={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        title={displayLabel || "Visual Diagram"}
        src={block.media_url || block.image_url}
        allowDownload={allowDownload}
        downloadFilename={`solution-diagram-${block.id || blockIndex + 1}`}
      >
        {!(block.media_url || block.image_url) && isSvgDiagramBlock(block) && (
          <div className="w-full flex items-center justify-center [&>div]:!max-w-none [&>div]:!w-full [&>div>div]:!max-w-none [&>div>div]:!w-full [&_svg]:!w-full [&_svg]:!max-h-[78vh] [&_svg]:!h-auto">
            <SvgDiagramRenderer
              content={block.svg_content || block.content || ""}
              title={displayLabel || "Visual Diagram"}
              allowFullscreen={false}
              allowDownload={false}
            />
          </div>
        )}
        {!(block.media_url || block.image_url) && !isSvgDiagramBlock(block) && content && (
          <div className="space-y-4">
            <KatexRenderer content={content} format={format} disableDirectionCallout={true} className="text-sm leading-relaxed" />
          </div>
        )}
      </FullscreenMediaModal>
    </div>
  )
}
